import { useCallback } from 'react'
import { useNavigate } from 'react-router'
import { deleteTask, restoreTask, setTaskScope, updateTask, type TaskPatch } from '@/data/tasks'
import { fire } from '@/lib/writeError'
import { doneOnce, kindChange, moveToDay, moveToTray, reopenOnce, waitingOff, waitingOn, type TrayGroup } from '@/logic/changes'
import { fmtDate, today } from '@/logic/dates'
import { applyCompletion, applyRoutineCompletion, undoCompletionPatch, untickCompletion } from '@/logic/recurrence'
import { useCategories } from '@/store/categories'
import { showToast } from '@/store/ui'
import type { Completion, DateStr, Scope, Task, TaskKind, TimeStr } from '@/types'
import { usePaths } from './usePaths'

const KIND_WORDS: Record<TaskKind, string> = { once: 'Now a one-off', repeat: 'Now repeating', routine: 'Now a routine' }

/**
 * The task actions every view shares: tick, reopen, Waiting, kind, planning a
 * day, the tray, delete with Undo, shared or private, and checklist steps.
 *
 * None waits for the server: Firestore applies each write locally at once and
 * syncs when there is a connection, so the toast and the next screen come
 * immediately, online or not. A refused write is reported by `fire`.
 */
export function useTaskActions() {
  const paths = usePaths()
  const navigate = useNavigate()

  const patch = useCallback((task: Task, change: TaskPatch) => fire(updateTask(paths, task, change)), [paths])

  /** Writes a change and offers Undo, which writes `before` over the changed task. */
  const withUndo = useCallback(
    (task: Task, change: TaskPatch, before: TaskPatch, text: string) => {
      fire(updateTask(paths, task, change))
      showToast(text, () => fire(updateTask(paths, { ...task, ...change }, before)))
    },
    [paths],
  )

  const complete = useCallback(
    (task: Task) => {
      const now = new Date()
      if (task.kind === 'repeat' && task.recurrence) {
        const { patch: change, next } = applyCompletion(task, now)
        withUndo(task, change, undoCompletionPatch(task), `Done. Next due ${fmtDate(next)}`)
        return
      }
      if (task.kind === 'routine') {
        const { patch: change } = applyRoutineCompletion(task, now)
        withUndo(task, change, undoCompletionPatch(task), 'Done for this week')
        return
      }
      const { patch: change, before } = doneOnce(task, today(now))
      withUndo(task, change, before, 'Done')
    },
    [withUndo],
  )

  const reopen = useCallback((task: Task) => patch(task, reopenOnce()), [patch])

  /** Takes back any tick of a repeat or a routine; like reopening a one-off, it has no toast. */
  const untick = useCallback(
    (task: Task, completion: Completion) => {
      const change = untickCompletion(task, completion)
      if (Object.keys(change).length) patch(task, change)
    },
    [patch],
  )

  /** A ring tapped; on a ticked row, `completion` is the tick it shows. */
  const toggleDone = useCallback(
    (task: Task, completion?: Completion | null) => {
      if (completion) untick(task, completion)
      else if (task.kind === 'once' && task.done) reopen(task)
      else complete(task)
    },
    [complete, reopen, untick],
  )

  /** Waiting on asks "Follow up when?" first; the caller passes the answer. */
  const setWaiting = useCallback(
    (task: Task, on: boolean, date: DateStr | null = null, time: TimeStr | null = null) => {
      patch(task, on ? waitingOn(date, time) : waitingOff())
    },
    [patch],
  )

  const setKind = useCallback(
    (task: Task, kind: TaskKind) => {
      const change = kindChange(task, kind, today())
      if (change) withUndo(task, change.patch, change.before, KIND_WORDS[kind])
    },
    [withUndo],
  )

  /** A drop on a day, or a Do it on day picked in the panel; `order` places it among the day's tasks. */
  const planOn = useCallback(
    (task: Task, day: DateStr, order?: number, toast = true) => {
      const change: TaskPatch = { ...moveToDay(task, day), ...(order === undefined ? {} : { order }) }
      const before: TaskPatch = { plannedDate: task.plannedDate, weekOf: task.weekOf, followUpDate: task.followUpDate, order: task.order }
      if (toast) withUndo(task, change, before, `Planned for ${fmtDate(day)}`)
      else patch(task, change)
    },
    [withUndo, patch],
  )

  /** A drop on a tray count or group, at `order` when dropped among its cards, or the Do it on day cleared. */
  const toTray = useCallback(
    (task: Task, group: TrayGroup, order?: number): boolean => {
      const result = moveToTray(task, group, today())
      if ('refused' in result) {
        showToast(result.refused)
        return false
      }
      const before: TaskPatch = { plannedDate: task.plannedDate, weekOf: task.weekOf, followUpDate: task.followUpDate, followUpTime: task.followUpTime, order: task.order }
      const text = result.toast === 'Back on its due day' && task.dueDate ? `Back on its due day, ${fmtDate(task.dueDate)}` : result.toast
      withUndo(task, { ...result.patch, ...(order === undefined ? {} : { order }) }, before, text)
      return true
    },
    [withUndo],
  )

  const remove = useCallback(
    (task: Task, closePanel = false) => {
      if (closePanel) navigate(-1)
      fire(deleteTask(paths, task))
      showToast('Deleted', () => fire(restoreTask(paths, task)))
    },
    [paths, navigate],
  )

  /** Moves a task between shared and private; going shared, it stays yours unless given to someone else at once. */
  const setScope = useCallback(
    (task: Task, scope: Scope, assigneeUid?: string | null) => {
      const privateIds = new Set(useCategories.getState().private.map((c) => c.id))
      fire(setTaskScope(paths, task, scope, privateIds, assigneeUid))
    },
    [paths],
  )

  const toggleStep = useCallback(
    (task: Task, stepId: string, done: boolean) => {
      const checklist = (task.checklist ?? []).map((s) => (s.id === stepId ? { ...s, done } : s))
      fire(updateTask(paths, task, { checklist }))
    },
    [paths],
  )

  return { complete, reopen, untick, toggleDone, setWaiting, setKind, planOn, toTray, remove, setScope, toggleStep, patch }
}
